import { DEFAULT_LANGUAGE, useLanguage } from "@next-platform/client-common-core";
import { Helmet } from "react-helmet-async";

import { ROUTES } from "@/constants";
import { useLocalizedUrl } from "@/hooks/useLocalizedUrl";

type RouteValue = (typeof ROUTES)[keyof typeof ROUTES];

type PageMetaTagsProps = {
  title?: string;
  description?: string;
  canonicalUrl?: RouteValue | (string & {});
  contentLanguage?: string;
  ogImage?: string;
  prerenderStatusCode?: number;
  "data-testid"?: string;
  jsonLd?: Record<string, unknown> | null;
};

function resolveCanonical(canonicalUrl: string | undefined, localizedUrl: ReturnType<typeof useLocalizedUrl>) {
  const routePath = canonicalUrl?.startsWith("/") ? canonicalUrl : undefined;
  const resolvedUrl = routePath ? localizedUrl({ route: routePath }) : canonicalUrl || localizedUrl();
  const showHreflang = !canonicalUrl || !!routePath || canonicalUrl.startsWith(window.location.origin);
  return { resolvedUrl, showHreflang, routePath };
}

const PageMetaTags = ({
  title,
  description,
  canonicalUrl,
  contentLanguage,
  ogImage,
  prerenderStatusCode,
  "data-testid": testId = "PageMetaTags",
  jsonLd,
}: PageMetaTagsProps) => {
  const { currentLanguage, availableLanguages } = useLanguage();
  const localizedUrl = useLocalizedUrl();

  const lang = contentLanguage || currentLanguage;
  const { resolvedUrl: resolvedCanonicalUrl, showHreflang, routePath } = resolveCanonical(canonicalUrl, localizedUrl);

  return (
    <div data-testid={testId} className="hidden">
      <Helmet>
        <html lang={lang} />
        {title ? <title>{title}</title> : null}
        {description ? <meta name="description" content={description} /> : null}
        {title ? <meta property="og:title" content={title} /> : null}
        {description ? <meta property="og:description" content={description} /> : null}
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        {ogImage ? <meta name="twitter:card" content="summary_large_image" /> : null}
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <link rel="canonical" href={resolvedCanonicalUrl} />
        {showHreflang ? (
          <>
            {Object.values(availableLanguages).map((language) => {
              const href = localizedUrl({ languageCode: language.code, route: routePath });
              return <link key={language.code} rel="alternate" hrefLang={language.code} href={href} />;
            })}
            <link rel="alternate" hrefLang="x-default" href={localizedUrl({ languageCode: DEFAULT_LANGUAGE, route: routePath })} />
          </>
        ) : null}
        {prerenderStatusCode ? <meta name="prerender-status-code" content={String(prerenderStatusCode)} /> : null}
        {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd).replace(/</g, "\\u003c")}</script> : null}
      </Helmet>
    </div>
  );
};

export default PageMetaTags;
